import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Check, Building, Home, Users, Compass, MoreHorizontal } from "lucide-react";

const Partner = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "", partner_type: "comune", message: "",
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/partner", { ...form, lang });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    }
  };

  const types = [
    { v: "comune", l: t("partner_type_comune"), icon: Building },
    { v: "alloggio", l: t("partner_type_alloggio"), icon: Home },
    { v: "gal", l: t("partner_type_gal"), icon: Users },
    { v: "guida", l: t("partner_type_guida"), icon: Compass },
    { v: "altro", l: t("partner_type_altro"), icon: MoreHorizontal },
  ];

  return (
    <div data-testid="partner-page">
      <SEO
        titleIt="Diventa Partner — Comuni, strutture e associazioni del Cammino"
        titleEn="Become a Partner — Municipalities, hosts and associations of the Trail"
        descriptionIt="Aderisci al Cammino Ibleo UNESCO come partner istituzionale, struttura ricettiva, produttore locale o associazione. Compila il modulo di adesione."
        descriptionEn="Join the Cammino Ibleo UNESCO as an institutional partner, accommodation, local producer or association. Fill out the partnership form."
      />
      <section className="pt-40 pb-16 bg-miele">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow text-carrubo-dark mb-6" style={{ color: "#2D4632" }}>
            <span style={{ color: "#2D4632" }}>{t("partner_eyebrow")}</span>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("partner_page_title")}</h1>
          <p className="text-lg text-grafite/85 max-w-3xl">{t("partner_page_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          {sent ? (
            <div data-testid="partner-success" className="p-16 bg-carrubo text-pietra-light rounded-sm text-center">
              <Check size={48} className="text-miele mx-auto mb-6" />
              <p className="font-serif text-3xl">{t("form_submit_success")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="partner-form" className="space-y-8 bg-pietra p-10 rounded-sm border border-carrubo/15">
              <div>
                <label className="small-caps text-miele mb-3 block">{t("partner_form_type")}</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {types.map((tp) => (
                    <button
                      key={tp.v}
                      type="button"
                      data-testid={`partner-type-${tp.v}`}
                      onClick={() => setForm({ ...form, partner_type: tp.v })}
                      className={`p-4 rounded-sm border-2 text-center text-xs font-semibold transition-all ${
                        form.partner_type === tp.v
                          ? "border-carrubo bg-carrubo text-pietra-light"
                          : "border-carrubo/15 bg-pietra-light text-grafite hover:border-carrubo/40"
                      }`}
                    >
                      <tp.icon size={20} className="mx-auto mb-2" strokeWidth={1.4} />
                      {tp.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label={t("partner_form_org")} value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="partner-org" required />
                <Field label={t("partner_form_name")} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="partner-name" required />
                <Field label={t("form_email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="partner-email" required />
                <Field label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="partner-phone" />
              </div>

              <div>
                <label className="small-caps text-grafite-soft mb-2 block">{t("partner_form_message")}</label>
                <textarea
                  required
                  data-testid="partner-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-4 outline-none text-grafite rounded-sm"
                />
              </div>

              {error && <div className="text-red-600 text-sm">{error}</div>}

              <button
                type="submit"
                data-testid="partner-submit"
                className="w-full bg-carrubo hover:bg-carrubo-dark text-pietra-light font-bold py-4 rounded-sm tracking-widest uppercase text-sm"
              >
                {t("form_submit")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="small-caps text-grafite-soft mb-2 block">{label}</label>
    <input
      type={type}
      required={required}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-3 outline-none text-grafite rounded-sm"
    />
  </div>
);

export default Partner;
